import { useState, useCallback, useRef, useEffect } from 'react';
import { useSourceChanged } from '@/hooks/useSourceChanged';
import type { SelectOption } from './selectTypes';

interface UseSelectListboxParams {
  /** Available options */
  options: SelectOption[];
  /** Currently selected value */
  selectedValue: string;
  /** Disabled state — schluckt die Tastaturbedienung */
  disabled?: boolean;
  /** Wird mit dem neuen Wert gerufen, sobald eine Option gewaehlt wurde */
  onValueChange: (value: string) => void;
}

/**
 * Bedienzustand der Auswahlliste: geoeffnet, hervorgehobener Eintrag,
 * Tastatursteuerung und Schliessen bei Klick daneben.
 *
 * Bewusst als Hook und nicht in der Komponente: Das ist abgeleiteter Zustand
 * samt Logik, die Komponente bleibt reine Darstellung.
 */
export const useSelectListbox = ({
  options,
  selectedValue,
  disabled,
  onValueChange,
}: UseSelectListboxParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Der Hook haelt den Ausloeser selbst fest (fuer Fokus und Klick-daneben).
  // Nach aussen geht nur diese Rueckruf-Referenz, damit der Aufrufer die
  // Referenz des Hooks nicht direkt beschreiben muss.
  const setTriggerNode = useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node;
  }, []);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onValueChange(optionValue);
      setIsOpen(false);
      triggerRef.current?.focus();
    },
    [onValueChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            const option = options[highlightedIndex];
            if (option && !option.disabled) {
              handleSelect(option.value);
            }
          } else {
            setIsOpen(true);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) => {
              const next = prev + 1;
              return next >= options.length ? 0 : next;
            });
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) => {
              const next = prev - 1;
              return next < 0 ? options.length - 1 : next;
            });
          }
          break;
        case 'Home':
          e.preventDefault();
          setHighlightedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setHighlightedIndex(options.length - 1);
          break;
      }
    },
    [disabled, isOpen, highlightedIndex, options, handleSelect]
  );

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        listRef.current &&
        !listRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Reset highlighted index when opening
  // Kein Effekt noetig: Die Hervorhebung leitet sich aus der Quelle (offen?
  // welche Optionen? welcher Wert?) ab und wird direkt beim Wechsel dieser
  // Quelle nachgezogen. So steht sie schon im ersten Render nach dem
  // Oeffnen richtig und nicht erst einen Durchlauf spaeter.
  if (useSourceChanged([isOpen, options, selectedValue]) && isOpen) {
    const selectedIndex = options.findIndex((opt) => opt.value === selectedValue);
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }

  return {
    isOpen,
    setIsOpen,
    highlightedIndex,
    setHighlightedIndex,
    setTriggerNode,
    listRef,
    handleSelect,
    handleKeyDown,
  };
};
